/* eslint-disable react-refresh/only-export-components */
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import api from "../config/axiosConfig";

const DEBUG = (window && typeof window !== "undefined") ? (window.__APP_DEBUG__ ?? true) : true;

/**
 * useFetch(path, { params, skip } = {})
 */
export default function useFetch(path, { params, skip } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(!skip && path));
  const [error, setError] = useState(null);

  // Create a stable params key by stringifying params with sorted keys
  // This allows us to compare params by value, not reference
  const paramsKey = useMemo(() => {
    if (!params) return "";
    // Sort keys to ensure consistent stringification
    const sorted = Object.keys(params).sort().reduce((acc, key) => {
      acc[key] = params[key];
      return acc;
    }, {});
    return JSON.stringify(sorted);
  }, [params]);

  const abortRef = useRef(null);
  const paramsRef = useRef(params);

  // Update paramsRef when paramsKey changes (values actually changed)
  // Include params in dependency to ensure paramsRef.current always points to latest params object
  // This is safe because fetchData only depends on paramsKey, not params directly
  useEffect(() => {
    paramsRef.current = params;
  }, [paramsKey, params]);

  // Accepts { silent: true } to refresh data without toggling `loading`
  // (used by pollers so the UI doesn't flash spinners). Safe when used as an
  // event handler: an event object has no `silent` property.
  const fetchData = useCallback(async (options) => {
    const silent = Boolean(options && options.silent === true);
    if (skip || !path) {
      DEBUG && console.info("⏭️ useFetch skipped:", { path, skip, params: paramsRef.current });
      return null;
    }


    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (!silent) setLoading(true);
    setError(null);

    try {
      const res = await api.get(path, { params: paramsRef.current, signal: controller.signal });
      setData(res.data);
      
      
      return res.data;
    } catch (e) {
      if (e.name === "CanceledError" || e.name === "AbortError") {
        // Request was aborted, don't set error or throw
        return null;
      } else {
        // Extract error message from the error object
        let errorMessage = 'خطا در درخواست';
        
        if (e.response?.data?.detail) {
          // Django REST framework error format
          errorMessage = e.response.data.detail;
        } else if (e.response?.data?.error) {
          errorMessage = e.response.data.error;
        } else if (e.response?.data?.message) {
          errorMessage = e.response.data.message;
        } else if (e.message) {
          errorMessage = e.message;
        }
        
        setError(errorMessage);
        throw e;
      }
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
        abortRef.current = null;
      }
      
    }
  }, [skip, path]); // fetchData doesn't need paramsKey since it uses paramsRef.current

  useEffect(() => {
    if (skip || !path) {
      setLoading(false);
      return;
    }
    fetchData().catch((error) => {
      // Only log non-canceled errors
      if (error.name !== "CanceledError" && error.name !== "AbortError") {
        console.error("useFetch error:", error);
      }
    });
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [fetchData, skip, path, paramsKey]); // Include paramsKey here to trigger refetch when params values change

  return { data, loading, error, refetch: fetchData };
}
